import { EmailProvenance } from "@/components/ContactChannels/emailProvenance";

import { ListPerson } from "../../pages/List/schema/list";
import { EmailFactsUpdate, ProspectUpdate } from "../../pages/List/schema/prospect-update";

/**
 * The email hover's facts an edit answers with (PREC-4262): the caller's company's provenance of
 * the current address lives on the list-person row (it is per company, never on the shared
 * prospect), and when the address was set lives on the prospect.
 *
 * A response that carries no `emailSource` key is from an older API and changes nothing; one that
 * carries it replaces all three facts, nulls included, so a hover never shows a source that
 * belonged to the address the edit just replaced.
 */
export function mergeEmailFacts(person: ListPerson, response: EmailFactsUpdate): void {
	if (!("emailSource" in response)) {
		return;
	}

	const row = person as ListPerson & EmailProvenance;

	row.emailSource = response.emailSource ?? null;
	row.emailVerifiedAt = response.emailVerifiedAt ?? null;
	row.emailVerificationResult = response.emailVerificationResult ?? null;

	if (person.prospect && response.emailSetAt !== undefined) {
		person.prospect.emailSetAt = response.emailSetAt ?? null;
	}
}

/** The `updateProspectDto` fields that are copied onto the prospect as they were sent. */
const COPIED_WHEN_PRESENT = ["firstName", "middleName", "lastName", "bookingLink"] as const;
const COPIED_WHEN_TRUTHY = ["name", "seniority", "jobTitleToSeniority", "linkedinUrl"] as const;

/**
 * Apply an "Edit contact info" answer to a list-person row: the edited fields, the statuses the API
 * persists for a typed phone or email (VALID), and the email facts the answer carries.
 * Extracted from the `updateProspect` matcher in `list.slice.ts`.
 */
export function applyProspectUpdate(person: ListPerson, payload: ProspectUpdate): void {
	const { prospect } = person;
	const dto = payload.updateProspectDto;

	if (!prospect) {
		return;
	}

	const target = prospect as Record<string, unknown>;

	COPIED_WHEN_PRESENT.filter((key) => dto[key] !== undefined).forEach((key) => {
		target[key] = dto[key];
	});
	COPIED_WHEN_TRUTHY.filter((key) => dto[key]).forEach((key) => {
		target[key] = dto[key];
	});

	if (dto.phone) {
		prospect.phone = dto.phone;
		// Manually entered phone numbers are persisted as VALID by the backend.
		prospect.phoneStatus = "VALID";
	}

	if (dto.email) {
		prospect.email = dto.email;
		// Manually entered emails are persisted as VALID by the backend.
		prospect.emailStatus = "VALID";
	}

	if (dto.gender !== undefined) {
		prospect.gender = dto.gender;
		prospect.genderProbability = dto.gender ? 100 : null;
	}

	mergeEmailFacts(person, payload);
}

/**
 * Apply an "Edit contact info" answer to the edited lead wherever the list views hold it: the inbox,
 * the search results and the campaign leads, like the pending-email confirm (review round 2, S2).
 */
export function applyProspectUpdateToRows(views: ListPerson[][], payload: ProspectUpdate): void {
	for (const rows of views) {
		const person = rows.find((row) => row.id === +payload.id);

		if (person) {
			applyProspectUpdate(person, payload);
		}
	}
}
